import PageBackTitle from "../../Component/PageBackTitle";
import React, { useEffect, useState } from "react";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import Select from "react-select";
import { TbRefresh } from "react-icons/tb";
import Pagination from "../../Component/Pagination";
import { hrRecordsApi } from "../../Services/hrRecordsApi";
import {
  buildSimpleOptions,
  formatDateOnlyIST,
  getDuePillStyle,
} from "../hrRecordsHelpers";
import "../hrRecords.css";
import "../../tables/User.css";
import "./trainingDashboard.css";

const WITHIN_DAYS_OPTIONS = [
  { value: 30, label: "Next 30 days" },
  { value: 60, label: "Next 60 days" },
  { value: 90, label: "Next 90 days" },
  { value: 180, label: "Next 180 days" },
  { value: 365, label: "Next 365 days" },
];

const INCLUDE_OVERDUE_OPTIONS = [
  { value: "true", label: "Include overdue" },
  { value: "false", label: "Due only (exclude overdue)" },
];

function TrainingDueOverduePage({ isSidebarExpanded }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [paginationKey, setPaginationKey] = useState(0);

  const [standards, setStandards] = useState([]);
  const [trainingNames, setTrainingNames] = useState([]);
  const [selectedStandardBody, setSelectedStandardBody] = useState(null);
  const [selectedTrainingName, setSelectedTrainingName] = useState(null);
  const [withinDays, setWithinDays] = useState(WITHIN_DAYS_OPTIONS[2]);
  const [includeOverdue, setIncludeOverdue] = useState(INCLUDE_OVERDUE_OPTIONS[0]);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const fetchLookups = async () => {
    try {
      const [standardsResponse, mastersResponse] = await Promise.all([
        hrRecordsApi.listTrainingStandards(),
        hrRecordsApi.listTrainingMasters({ page: 1, per_page: 200 }),
      ]);

      setStandards(
        buildSimpleOptions(standardsResponse || [], "standard_body", "display_name"),
      );

      const uniqueNames = Array.from(
        new Set((mastersResponse.items || []).map((master) => master.training_name).filter(Boolean)),
      );
      setTrainingNames(uniqueNames.map((name) => ({ value: name, label: name })));
    } catch (error) {
      console.error("Failed to load due/overdue filters:", error);
    }
  };

  const fetchRecords = async (
    currentPage = page,
    currentItemsPerPage = itemsPerPage,
  ) => {
    try {
      setLoading(true);
      const response = await hrRecordsApi.listTrainingDue({
        within_days: withinDays?.value || 90,
        include_overdue: includeOverdue?.value !== "false",
        standard_body: selectedStandardBody?.value || undefined,
        training_name: selectedTrainingName?.value || undefined,
        page: currentPage,
        per_page: currentItemsPerPage,
      });

      setRecords(response.items || []);
      setTotalItems(response.total_items || 0);
    } catch (error) {
      console.error("Failed to load due and overdue trainings:", error);
      showSnackbar("Failed to load due and overdue trainings", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLookups();
  }, []);

  useEffect(() => {
    fetchRecords(page, itemsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, itemsPerPage, selectedStandardBody, selectedTrainingName, withinDays, includeOverdue]);

  const resetToFirstPage = () => {
    setPage(1);
    setPaginationKey((key) => key + 1);
  };

  const handleRefresh = () => {
    setSelectedStandardBody(null);
    setSelectedTrainingName(null);
    setWithinDays(WITHIN_DAYS_OPTIONS[2]);
    setIncludeOverdue(INCLUDE_OVERDUE_OPTIONS[0]);
    resetToFirstPage();
  };

  const formatDaysUntilDue = (days) => {
    if (days === null || days === undefined) {
      return "NA";
    }

    return days < 0 ? `${Math.abs(days)}d overdue` : `${days}d`;
  };

  return (
    <>
      <div
        className={`data-list-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-list-content">
          <div className="data-list-header">
            <div className="data-list-header-content">
              <PageBackTitle>Training Due &amp; Overdue</PageBackTitle>
              <div className="data-list-heading-content-right-side hr-records-toolbar">
                <div className="dropdown-container" style={{ minWidth: "190px" }}>
                  <Select
                    options={standards}
                    value={selectedStandardBody}
                    onChange={(option) => {
                      setSelectedStandardBody(option);
                      resetToFirstPage();
                    }}
                    placeholder="Filter by Standard Body"
                    isSearchable
                    isClearable
                  />
                </div>
                <div className="dropdown-container" style={{ minWidth: "190px" }}>
                  <Select
                    options={trainingNames}
                    value={selectedTrainingName}
                    onChange={(option) => {
                      setSelectedTrainingName(option);
                      resetToFirstPage();
                    }}
                    placeholder="Filter by Training"
                    isSearchable
                    isClearable
                  />
                </div>
                <div className="dropdown-container" style={{ minWidth: "160px" }}>
                  <Select
                    options={WITHIN_DAYS_OPTIONS}
                    value={withinDays}
                    onChange={(option) => {
                      setWithinDays(option || WITHIN_DAYS_OPTIONS[2]);
                      resetToFirstPage();
                    }}
                    placeholder="Within days"
                  />
                </div>
                <div className="dropdown-container" style={{ minWidth: "210px" }}>
                  <Select
                    options={INCLUDE_OVERDUE_OPTIONS}
                    value={includeOverdue}
                    onChange={(option) => {
                      setIncludeOverdue(option || INCLUDE_OVERDUE_OPTIONS[0]);
                      resetToFirstPage();
                    }}
                  />
                </div>
                <Tooltip title="Refresh" arrow>
                  <button className="refresh-button" onClick={handleRefresh}>
                    <TbRefresh className="refresh-icon" />
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>
          <div className="header-separator"></div>

          <div className="data-list-body">
            <div className="data-list-scroll-body">
              <div className="data-list-table">
                {loading ? (
                  <div className="loader-container">
                    <div className="loader"></div>
                    <div>Loading...</div>
                  </div>
                ) : records.length > 0 ? (
                  <table className="table multilevel-data">
                    <thead>
                      <tr>
                        <th>SR. NO.</th>
                        <th>EMPLOYEE</th>
                        <th>TRAINING</th>
                        <th>STANDARD</th>
                        <th>SESSION</th>
                        <th>ISSUE DATE</th>
                        <th>NEXT DUE</th>
                        <th>DAYS</th>
                        <th>STATUS</th>
                      </tr>
                    </thead>
                    <tbody className="data-list-scroll-bodys">
                      {records.map((item, index) => (
                        <tr
                          key={item.participant_id}
                          className={`hoverable-row ${
                            item.days_until_due < 0 ? "htd-row-overdue" : ""
                          }`}
                        >
                          <td>{(page - 1) * itemsPerPage + index + 1}</td>
                          <td>
                            {item.person_name || "NA"}
                            {item.ec_no && (
                              <>
                                <br />
                                <span className="htd-muted-text">
                                  {item.ec_no}
                                </span>
                              </>
                            )}
                          </td>
                          <td>{item.training_name || "NA"}</td>
                          <td>
                            {item.standard_body || "NA"}
                            {item.standard_ref_no && (
                              <>
                                <br />
                                <span className="htd-muted-text">
                                  {item.standard_ref_no}
                                </span>
                              </>
                            )}
                          </td>
                          <td>{item.session_code || "NA"}</td>
                          <td>{formatDateOnlyIST(item.issue_date)}</td>
                          <td>{formatDateOnlyIST(item.next_due_date)}</td>
                          <td>{formatDaysUntilDue(item.days_until_due)}</td>
                          <td className="center-alignment">
                            <span
                              className="hr-records-pill"
                              style={getDuePillStyle(item.status)}
                            >
                              {(item.status || "NA").replace(/_/g, " ")}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-users-container">
                    <div className="no-users-message">
                      No due or overdue trainings found
                    </div>
                  </div>
                )}
              </div>

              <Pagination
                key={paginationKey}
                showPerPage={itemsPerPage}
                total={totalItems}
                onPaginationChange={(currentPage, perPage) => {
                  setPage(currentPage);
                  setItemsPerPage(perPage);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert severity={snackbarSeverity}>{snackbarMessage}</MuiAlert>
      </Snackbar>
    </>
  );
}

export default TrainingDueOverduePage;
